import { useEffect, useState, type FormEvent } from 'react';
import { Link, Navigate, useNavigate, useParams } from 'react-router-dom';
import type { AutomationFlowStatus, FlowDefinition } from '@neuroedge/shared';
import { Button, Card, PageState, Select, TextField, useToast } from '../../components/ui';
import { ApiClientError } from '../../api/client';
import { fieldErrorsFromApiError } from '../../lib/field-errors';
import { useAuth } from '../../auth/AuthContext';
import {
  useAdminAutomationFlow,
  useCreateAutomationFlow,
  useUpdateAutomationFlow,
} from '../../api/automation-flows';
import { LazyFlowDefinitionEditor } from './builder/LazyFlowDefinitionEditor';
import { flowProblems } from './builder/flow-graph';

const KEY_RE = /^[a-z0-9_]+$/;

const STATUS_OPTIONS: { value: AutomationFlowStatus; label: string }[] = [
  { value: 'ACTIVE', label: 'Active (selectable from a "Start a flow" action)' },
  { value: 'ARCHIVED', label: 'Archived (existing automations using it will fail loudly)' },
];

/** A new flow starts as a two-button question with two endings - enough to see how the canvas works. */
const STARTER_DEFINITION: FlowDefinition = {
  startStepId: 'greet',
  steps: {
    greet: {
      kind: 'SEND_MESSAGE',
      text: 'Would you like a callback?',
      buttons: [
        { id: 'yes', title: 'Yes' },
        { id: 'no', title: 'No' },
      ],
      transitions: { yes: 'yes_end', no: 'no_end' },
    },
    yes_end: { kind: 'END', text: "Great, we'll call you back soon!" },
    no_end: { kind: 'END', text: 'No worries!' },
  },
};

/**
 * Platform-admin create/edit for a system conversation flow. The step graph
 * is edited on the visual drag-and-drop canvas (`FlowDefinitionEditor` ->
 * `FlowBuilder`), with raw JSON one click away under "Advanced". The server
 * still fully validates the graph (every target must exist, etc.).
 */
export function AutomationFlowAdminFormPage() {
  const { id } = useParams<{ id: string }>();
  const isEdit = Boolean(id);
  const { isPlatformAdmin } = useAuth();
  const navigate = useNavigate();
  const toast = useToast();

  const existing = useAdminAutomationFlow(id);
  const create = useCreateAutomationFlow();
  const update = useUpdateAutomationFlow(id ?? '');

  const [key, setKey] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [status, setStatus] = useState<AutomationFlowStatus>('ACTIVE');
  const [definition, setDefinition] = useState<FlowDefinition>(STARTER_DEFINITION);
  const [jsonValid, setJsonValid] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});

  useEffect(() => {
    if (isEdit && existing.data) {
      const f = existing.data;
      setKey(f.key);
      setName(f.name);
      setDescription(f.description ?? '');
      setStatus(f.status);
      setDefinition(f.definition);
    }
  }, [isEdit, existing.data]);

  if (!isPlatformAdmin) return <Navigate to="/" replace />;
  if (!existing.isError && isEdit && existing.isPending) {
    return <PageState kind="loading" message="Loading flow…" />;
  }
  if (isEdit && existing.isError)
    return <PageState kind="error" message={existing.error.message} />;

  const busy = create.isPending || update.isPending;

  function clearFieldError(k: string) {
    setFieldErrors((f) => {
      if (!f[k]) return f;
      const { [k]: _removed, ...rest } = f;
      return rest;
    });
  }

  function validate(): Record<string, string> {
    const errors: Record<string, string> = {};

    const trimmedKey = key.trim();
    if (trimmedKey.length < 1) errors.key = 'Enter a key.';
    else if (trimmedKey.length > 120) errors.key = 'Key must be 120 characters or fewer.';
    else if (!KEY_RE.test(trimmedKey)) {
      errors.key = 'Use lowercase letters, numbers, and underscores only (e.g. my_flow).';
    }

    const trimmedName = name.trim();
    if (trimmedName.length < 1) errors.name = 'Enter a name.';
    else if (trimmedName.length > 200) errors.name = 'Name must be 200 characters or fewer.';

    if (description.trim().length > 2000) {
      errors.description = 'Description must be 2000 characters or fewer.';
    }

    const problems = flowProblems(jsonValid ? definition : null);
    if (problems.length) errors.definition = problems.join(' ');
    return errors;
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    const errors = validate();
    setFieldErrors(errors);
    if (Object.keys(errors).length > 0) {
      setError('Please fix the highlighted fields.');
      toast.error('Please fix the highlighted fields.');
      return;
    }

    const payload = {
      key: key.trim(),
      name: name.trim(),
      description: description.trim() || null,
      status,
      definition,
    };

    try {
      if (isEdit && id) {
        await update.mutateAsync(payload);
      } else {
        await create.mutateAsync(payload);
      }
      toast.success(isEdit ? 'Flow updated.' : 'Flow created.');
      navigate('/admin/automation-flows', { replace: true });
    } catch (err) {
      if (err instanceof ApiClientError && err.code === 'VALIDATION_FAILED') {
        const serverFieldErrors = fieldErrorsFromApiError(err);
        if (Object.keys(serverFieldErrors).length > 0) {
          const mapped: Record<string, string> = {};
          for (const [k, message] of Object.entries(serverFieldErrors)) {
            mapped[k.startsWith('definition') ? 'definition' : k] = message;
          }
          setFieldErrors(mapped);
          setError('Please fix the highlighted fields.');
          toast.error('Please fix the highlighted fields.');
        } else {
          setError(err.message);
          toast.error(err.message);
        }
      } else {
        const message =
          err instanceof ApiClientError && err.httpStatus < 500
            ? err.message
            : 'Could not save the flow.';
        setError(message);
        toast.error(message);
      }
    }
  }

  return (
    <form onSubmit={onSubmit} noValidate style={{ display: 'grid', gap: 'var(--space-4)' }}>
      <h1 style={{ margin: 0, fontSize: 'var(--font-size-xl)' }}>
        {isEdit ? 'Edit flow' : 'New flow'}
      </h1>
      <Card>
        <div
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
            gap: 'var(--space-3)',
          }}
        >
          <TextField
            label="Key"
            placeholder="appointment_booking"
            value={key}
            onChange={(e) => {
              setKey(e.target.value);
              clearFieldError('key');
            }}
            error={fieldErrors.key}
          />
          <TextField
            label="Name"
            value={name}
            onChange={(e) => {
              setName(e.target.value);
              clearFieldError('name');
            }}
            error={fieldErrors.name}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={fieldErrors.description}
          />
          <Select
            label="Status"
            value={status}
            onChange={(e) => setStatus(e.target.value as AutomationFlowStatus)}
            options={STATUS_OPTIONS}
          />
        </div>
      </Card>

      <LazyFlowDefinitionEditor
        platformSteps
        value={definition}
        onChange={(next) => {
          setDefinition(next);
          clearFieldError('definition');
        }}
        onJsonValidityChange={setJsonValid}
        error={fieldErrors.definition}
      />

      {error ? (
        <p className="ne-auth__error" role="alert">
          {error}
        </p>
      ) : null}
      <div style={{ display: 'flex', gap: 'var(--space-3)' }}>
        <Button type="submit" disabled={busy}>
          {busy ? 'Saving…' : isEdit ? 'Save changes' : 'Create flow'}
        </Button>
        <Button
          type="button"
          variant="secondary"
          onClick={() => navigate('/admin/automation-flows')}
        >
          Cancel
        </Button>
      </div>
      <p style={{ margin: 0, fontSize: 'var(--font-size-sm)' }}>
        <Link to="/admin/automation-flows">← All flows</Link>
      </p>
    </form>
  );
}
